import { Room, RoomEvent, type RemoteParticipant as LkRemote, type Participant, DataPacket_Kind } from 'livekit-client';
import type { RoomTransport, RemoteParticipant } from '../core/types.js';
import type { DataChannelTopic } from '@gonzovnc/protocol';

/**
 * RoomTransport implemented over livekit-client (§5). This is the only place the web
 * Client touches the LiveKit SDK for data/attributes/metadata; media rendering is in
 * the UI. core/ never imports this file (DOM-free rule).
 */
export class LiveKitTransport implements RoomTransport {
  constructor(public room: Room) {}

  static async connect(wsUrl: string, token: string): Promise<LiveKitTransport> {
    // adaptiveStream is disabled: the screen-share stage <video> can measure 0x0 before its first
    // frame, which would make adaptiveStream pause the track (black stage).
    // dynacast is a PUBLISHER-side option (livekit-client reads roomOptions.dynacast only on the
    // publish path), and this client publishes no video — so it does nothing here. Layer selection
    // is done by RemoteTrackPublication.setVideoQuality(); the layers themselves are managed by the
    // HOST's RoomOptions(dynacast: true).
    const room = new Room({ adaptiveStream: false });
    await room.connect(wsUrl, token);
    return new LiveKitTransport(room);
  }

  localIdentity(): string {
    return this.room.localParticipant.identity;
  }

  publishData(topic: DataChannelTopic, payload: Uint8Array, opts: { reliable: boolean; destinationIdentities?: string[] }): void {
    // Swallow rejections: a publish racing a disconnect (e.g. a delayed hand re-announce firing
    // after the room closed) must not surface as an unhandled promise rejection.
    this.room.localParticipant.publishData(payload, {
      reliable: opts.reliable,
      destinationIdentities: opts.destinationIdentities,
      topic,
    }).catch(() => undefined);
  }

  onData(handler: (topic: string, payload: Uint8Array, from: string) => void): () => void {
    const fn = (payload: Uint8Array, participant?: LkRemote, _kind?: DataPacket_Kind, topic?: string) => {
      handler(topic ?? '', payload, participant?.identity ?? 'server');
    };
    this.room.on(RoomEvent.DataReceived, fn);
    return () => { this.room.off(RoomEvent.DataReceived, fn); };
  }

  onParticipant(handler: (p: RemoteParticipant, left: boolean) => void): () => void {
    const onConn = (p: LkRemote) => handler(this.toRemote(p), false);
    const onLeft = (p: LkRemote) => handler(this.toRemote(p), true);
    this.room.on(RoomEvent.ParticipantConnected, onConn);
    this.room.on(RoomEvent.ParticipantDisconnected, onLeft);
    return () => {
      this.room.off(RoomEvent.ParticipantConnected, onConn);
      this.room.off(RoomEvent.ParticipantDisconnected, onLeft);
    };
  }

  onAttributesChanged(handler: (identity: string, attributes: Record<string, string>) => void): () => void {
    const fn = (_changed: Record<string, string>, participant: Participant) => {
      handler(participant.identity, participant.attributes ?? {});
    };
    this.room.on(RoomEvent.ParticipantAttributesChanged, fn);
    return () => { this.room.off(RoomEvent.ParticipantAttributesChanged, fn); };
  }

  onRoomMetadata(handler: (metadata: string) => void): () => void {
    const fn = (metadata: string) => handler(metadata);
    this.room.on(RoomEvent.RoomMetadataChanged, fn);
    return () => { this.room.off(RoomEvent.RoomMetadataChanged, fn); };
  }

  participants(): RemoteParticipant[] {
    const out: RemoteParticipant[] = [...this.room.remoteParticipants.values()].map((p) => this.toRemote(p));
    const lp = this.room.localParticipant;
    out.push({ identity: lp.identity, name: lp.name ?? lp.identity, attributes: lp.attributes ?? {} });
    return out;
  }

  private toRemote(p: Participant): RemoteParticipant {
    return { identity: p.identity, name: p.name ?? p.identity, attributes: p.attributes ?? {} };
  }

  async disconnect(): Promise<void> {
    await this.room.disconnect();
  }
}
